import { useMemo } from 'react';

import { useOrganization } from '../../../contexts/OrganizationContext';
import { AppExperiments, useIsTreatment } from '../../../utils/experiments';
import type { MemberRole, MemberRoleMap, MemberRolesForEntity } from '../utils/constants';
import { withImpliedRoles } from '../utils/roleHierarchy';

import { useLocalizedProducts } from './useLocalizedProducts';
import { useMultiLEProductAccessEntities } from './useMultiLEProductAccessEntities';
import type { EntityIdToProductSelections, ProductSelections } from './useMultiLEProductSelection';
import { useMultiLEProductSelection } from './useMultiLEProductSelection';
import { getProductKey, useRoles } from './useRoles';

type UseMultiLEMemberAccessParams = {
  /** The roles held (or invited) per legal entity; they seed the selection. */
  memberRolesForEntity: MemberRolesForEntity[] | undefined;
  /** Per-entity editability, see `useMultiLEProductAccessEntities`. */
  isEntityEditable?: (legalEntityId: string) => boolean;
  /** Changing it re-seeds the selection from `memberRolesForEntity`. */
  resetKey?: number;
};

/**
 * The multi-entity access group of the edit member tray: the selection seeded from
 * `memberRolesForEntity` and the `MultiLEProductAccessGroup` entities built from it.
 * `MultiLEEditTray` and `PendingInviteRoles` share it so both render the same rows.
 */
export function useMultiLEMemberAccess({
  memberRolesForEntity,
  isEntityEditable,
  resetKey,
}: UseMultiLEMemberAccessParams) {
  const { selectedOrganization } = useOrganization();
  const { supportedRoles, rolesByProductPerEntity, roleMap } = useRoles();
  // IDFE-4045: roles are hierarchical, so held roles are seeded with the roles
  // they imply (Admin also checks Viewer).
  const isHierarchicalRolesTreatment = useIsTreatment(AppExperiments.t0018_4fba688a);

  const legalEntityIds = useMemo(
    () =>
      (selectedOrganization?.legalEntities ?? [])
        .map((le) => le.legalEntityId)
        .filter((id): id is string => Boolean(id)),
    [selectedOrganization?.legalEntities],
  );

  const localizedProducts = useLocalizedProducts();

  const supportedRolesSet = useMemo(() => new Set(supportedRoles), [supportedRoles]);

  const initialEntitySelections = useMemo(() => {
    const result: EntityIdToProductSelections = {};
    for (const ler of memberRolesForEntity ?? []) {
      if (ler.legalEntityId) {
        const productSelections: ProductSelections = {};
        for (const role of (ler.roles ?? []) as MemberRole[]) {
          if (supportedRolesSet.has(role)) {
            const key = getProductKey(role);
            if (key) {
              (productSelections[key] ??= []).push(role);
            }
          }
        }
        if (isHierarchicalRolesTreatment) {
          const entityRolesByProduct = rolesByProductPerEntity[ler.legalEntityId] ?? {};
          for (const [key, roles] of Object.entries(productSelections)) {
            productSelections[key] = withImpliedRoles(roles ?? [], entityRolesByProduct[key] ?? []);
          }
        }
        result[ler.legalEntityId] = productSelections;
      }
    }
    return result;
  }, [
    isHierarchicalRolesTreatment,
    memberRolesForEntity,
    rolesByProductPerEntity,
    supportedRolesSet,
  ]);

  // Per-entity, per-product roles the member holds that are no longer assignable
  // (e.g. off-flag coinbiz_admin). Surfaced in the picker as checked + disabled;
  // the assignable-only rolesByProductPerEntity omits them.
  const unassignableRolesByProductPerEntity = useMemo(() => {
    const result: Record<string, Record<string, MemberRoleMap[]>> = {};
    for (const ler of memberRolesForEntity ?? []) {
      if (ler.legalEntityId) {
        const byProduct: Record<string, MemberRoleMap[]> = {};
        for (const role of (ler.roles ?? []) as MemberRole[]) {
          const definition = roleMap[role];
          if (definition && !definition.assignable) {
            (byProduct[definition.productKey] ??= []).push(definition);
          }
        }
        result[ler.legalEntityId] = byProduct;
      }
    }
    return result;
  }, [memberRolesForEntity, roleMap]);

  const selection = useMultiLEProductSelection({
    legalEntityIds,
    rolesByProductPerEntity,
    initialEntitySelections,
    resetKey,
  });

  const entities = useMultiLEProductAccessEntities({
    legalEntities: selectedOrganization?.legalEntities ?? [],
    localizedProducts,
    rolesByProductPerEntity,
    unassignableRolesByProductPerEntity,
    isEntityEditable,
    selection,
  });

  return {
    legalEntityIds,
    supportedRolesSet,
    unassignableRolesByProductPerEntity,
    selection,
    entities,
  };
}
